import { ArrowLeft, ChevronDown, Download, ImageUp, KeyRound, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../../api';
import { t } from '../../i18n';
import { PRAYER_ORDER } from '../../types';
import type {
  AssignmentDTO,
  ChildSettingsRow,
  ColorRamp,
  MemorizationConfig,
  MemorizationSlot,
  ModuleType,
  PrayerTime,
  RecitationConfig,
  SettingsResponse,
  SurahMeta,
} from '../../types';
import { AppShell } from '../shared/AppShell';
import { Avatar } from '../shared/Avatar';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
import { Modal } from '../shared/Modal';
import { useToast } from '../shared/Toast';

const RAMPS: ColorRamp[] = ['teal', 'blue', 'coral', 'purple', 'amber'];

export function Settings() {
  const navigate = useNavigate();
  const toast = useToast();
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [surahs, setSurahs] = useState<SurahMeta[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [pinChild, setPinChild] = useState<ChildSettingsRow | null>(null);
  const [deleteChild, setDeleteChild] = useState<ChildSettingsRow | null>(null);
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [parentPinOpen, setParentPinOpen] = useState(false);
  const [restDaysOpen, setRestDaysOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const refresh = () => api.parentSettings().then(setSettings);

  const saveAssignment = async (
    childId: number,
    moduleType: ModuleType,
    body: { is_active?: boolean; config?: RecitationConfig | MemorizationConfig },
  ) => {
    try {
      await api.updateAssignment(childId, moduleType, body);
      toast.show('Saqlandi', 'success');
      void refresh();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('common.error.network'), 'danger');
    }
  };

  useEffect(() => {
    void refresh();
    api.surahs().then((r) => setSurahs(r.surahs));
  }, []);

  const startExport = async () => {
    setExporting(true);
    try {
      const r = await api.startExport();
      toast.show('Tayyor', 'success');
      const a = document.createElement('a');
      a.href = r.download_url;
      a.download = '';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.show(t('common.error.network'), 'danger');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppShell>
      <main className="mx-auto flex min-h-dvh max-w-2xl flex-col gap-5 p-5 pb-12">
        <header className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} aria-label={t('common.back')} className="text-ink-muted">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-[18px] font-medium">{t('common.settings')}</h1>
        </header>

        <section className="flex flex-col gap-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
            {t('settings.children')}
          </div>
          {settings ? (
            <>
              {settings.children.map((c) => (
                <ChildSettingsCard
                  key={c.id}
                  row={c}
                  surahs={surahs}
                  isOpen={openId === c.id}
                  onToggle={() => setOpenId(openId === c.id ? null : c.id)}
                  onSave={async (patch) => {
                    try {
                      await api.patchChild(c.id, patch);
                      toast.show('Saqlandi', 'success');
                      void refresh();
                    } catch (err) {
                      toast.show(err instanceof ApiError ? err.message : t('common.error.network'), 'danger');
                    }
                  }}
                  onChangePin={() => setPinChild(c)}
                  onDelete={settings.children.length > 1 ? () => setDeleteChild(c) : undefined}
                  saveAssignment={saveAssignment}
                />
              ))}
              <Button variant="ghost" onClick={() => setAddChildOpen(true)} fullWidth>
                <Plus size={16} className="mr-2" />
                {t('settings.add_child')}
              </Button>
            </>
          ) : (
            <Card>
              <div className="h-12 animate-pulse rounded bg-surface-muted" />
            </Card>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
            {t('settings.parent')}
          </div>
          <Card>
            <Button variant="secondary" onClick={() => setParentPinOpen(true)} fullWidth>
              <KeyRound size={16} className="mr-2" />
              {t('settings.change_pin')}
            </Button>
            <Button variant="ghost" onClick={() => setRestDaysOpen(true)} fullWidth className="mt-2">
              {t('settings.rest_days')}
            </Button>
          </Card>
        </section>

        <section className="flex flex-col gap-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
            {t('settings.other')}
          </div>
          <Card>
            <Button onClick={startExport} disabled={exporting} fullWidth>
              <Download size={16} className="mr-2" />
              {exporting ? t('common.loading') : t('settings.export')}
            </Button>
            <p className="mt-3 text-[11px] text-ink-subtle">v0.0.1</p>
          </Card>
        </section>
      </main>

      {pinChild ? (
        <ChildPinModal
          child={pinChild}
          onClose={() => setPinChild(null)}
          onSaved={() => {
            setPinChild(null);
            void refresh();
            toast.show('Saqlandi', 'success');
          }}
        />
      ) : null}

      {deleteChild ? (
        <DeleteChildModal
          child={deleteChild}
          onClose={() => setDeleteChild(null)}
          onDeleted={() => {
            setDeleteChild(null);
            void refresh();
            toast.show(t('common.deleted'), 'success');
          }}
        />
      ) : null}

      <AddChildModal
        open={addChildOpen}
        onClose={() => setAddChildOpen(false)}
        onCreated={() => {
          setAddChildOpen(false);
          void refresh();
          toast.show(t('common.added'), 'success');
        }}
      />

      <ParentPinModal
        open={parentPinOpen}
        onClose={() => setParentPinOpen(false)}
        onSaved={() => {
          setParentPinOpen(false);
          void refresh();
          toast.show('Saqlandi', 'success');
        }}
      />

      <RestDaysModal
        open={restDaysOpen}
        childIds={settings?.children.map((c) => ({ id: c.id, name: c.name })) ?? []}
        onClose={() => setRestDaysOpen(false)}
      />
    </AppShell>
  );
}

type Patch = Parameters<typeof api.patchChild>[1];

function ChildSettingsCard({
  row,
  surahs,
  isOpen,
  onToggle,
  onSave,
  onChangePin,
  onDelete,
  saveAssignment,
}: {
  row: ChildSettingsRow;
  surahs: SurahMeta[];
  isOpen: boolean;
  onToggle: () => void;
  onSave: (patch: Patch) => void;
  onChangePin: () => void;
  onDelete?: () => void;
  saveAssignment: (
    childId: number,
    moduleType: ModuleType,
    body: { is_active?: boolean; config?: RecitationConfig | MemorizationConfig },
  ) => Promise<void>;
}) {
  const [name, setName] = useState(row.name);
  const [age, setAge] = useState(String(row.age));
  const [ramp, setRamp] = useState<ColorRamp>(row.color_ramp);
  const [initial, setInitial] = useState(row.avatar_initial);
  const [rule, setRule] = useState(row.daily_rule_text);
  const [surah, setSurah] = useState(row.current_surah_number);

  useEffect(() => {
    setName(row.name);
    setAge(String(row.age));
    setRamp(row.color_ramp);
    setInitial(row.avatar_initial);
    setRule(row.daily_rule_text);
    setSurah(row.current_surah_number);
  }, [row]);

  const dirty =
    name !== row.name ||
    Number(age) !== row.age ||
    ramp !== row.color_ramp ||
    initial !== row.avatar_initial ||
    rule !== row.daily_rule_text ||
    surah !== row.current_surah_number;

  const save = () => {
    const patch: Patch = {};
    if (name !== row.name) patch.name = name;
    if (Number(age) !== row.age) patch.age = Number(age);
    if (ramp !== row.color_ramp) patch.color_ramp = ramp;
    if (initial !== row.avatar_initial) patch.avatar_initial = initial;
    if (rule !== row.daily_rule_text) patch.daily_rule_text = rule;
    if (surah !== row.current_surah_number) patch.current_surah_number = surah;
    onSave(patch);
  };

  const recAssignment = row.assignments.find((a) => a.module_type === 'recitation');
  const memAssignment = row.assignments.find((a) => a.module_type === 'memorization');

  return (
    <Card>
      <button onClick={onToggle} className="flex w-full items-center gap-3">
        <div className={`ramp-${row.color_ramp}`} onClick={(e) => e.stopPropagation()}>
          <Avatar
            initial={row.avatar_initial}
            ramp={row.color_ramp}
            size="md"
            src={row.avatar_url ?? undefined}
          />
        </div>
        <div className="flex flex-1 flex-col text-left">
          <span className="text-[15px] font-medium">{row.name}</span>
          <span className="text-[12px] text-ink-muted">{row.age} yosh</span>
        </div>
        <ChevronDown size={18} className={`text-ink-muted transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen ? (
        <div className="mt-4 flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Profil rasmi" full>
            <AvatarUploader row={row} />
          </Field>
          <Field label="Ismi">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
              className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px]"
            />
          </Field>
          <Field label="Yoshi">
            <input
              type="number"
              min={1}
              max={99}
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px] tabular-nums"
            />
          </Field>
          <Field label="Avatar">
            <input
              value={initial}
              onChange={(e) => setInitial(e.target.value.slice(0, 1).toUpperCase())}
              maxLength={1}
              className="h-11 w-12 rounded-md border-hairline border-border-strong bg-surface px-3 text-center text-[16px] font-medium uppercase"
            />
          </Field>
          <Field label="Rang">
            <div className="flex gap-2">
              {RAMPS.map((r) => (
                <button
                  key={r}
                  onClick={() => setRamp(r)}
                  aria-label={r}
                  className={[
                    'ramp-' + r,
                    'h-9 w-9 rounded-full bg-child-accent transition',
                    ramp === r ? 'ring-2 ring-info ring-offset-2 ring-offset-bg' : 'opacity-70',
                  ].join(' ')}
                />
              ))}
            </div>
          </Field>
          <Field label="Qoida" full>
            <input
              value={rule}
              onChange={(e) => setRule(e.target.value)}
              maxLength={200}
              className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px]"
            />
          </Field>
          <Field label="Joriy sura" full>
            <select
              value={surah}
              onChange={(e) => setSurah(Number(e.target.value))}
              className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px]"
            >
              {surahs.map((s) => (
                <option key={s.number} value={s.number}>
                  {s.number}. {s.name_uz}
                </option>
              ))}
            </select>
          </Field>

          <div className="sm:col-span-2 flex flex-col gap-2 pt-1 sm:flex-row">
            <Button variant="secondary" onClick={onChangePin} fullWidth>
              <KeyRound size={16} className="mr-2" />
              {t('settings.change_pin')}
            </Button>
            <Button onClick={save} disabled={!dirty} fullWidth>
              {t('common.save')}
            </Button>
          </div>
          {onDelete ? (
            <div className="sm:col-span-2">
              <button
                onClick={onDelete}
                className="flex items-center gap-2 text-[12px] text-danger active:scale-[0.98]"
              >
                <Trash2 size={14} />
                {t('settings.delete_child')}
              </button>
            </div>
          ) : null}
          <div className="text-[11px] text-ink-subtle">
            PIN o'zgartirilgan: {new Date(row.pin_last_changed).toLocaleDateString('uz-UZ', { timeZone: 'Asia/Tashkent' })}
          </div>
          </div>

          <RecitationTargets
            key={`rec-${JSON.stringify(recAssignment?.config ?? null)}`}
            assignment={recAssignment}
            onSave={(body) => saveAssignment(row.id, 'recitation', body)}
          />
          <MemorizationSettings
            key={`mem-${JSON.stringify(memAssignment ?? null)}`}
            assignment={memAssignment}
            surahs={surahs}
            onSave={(body) => saveAssignment(row.id, 'memorization', body)}
          />
        </div>
      ) : null}
    </Card>
  );
}

function clampTarget(v: string | number): number {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(50, n));
}

function RecitationTargets({
  assignment,
  onSave,
}: {
  assignment: AssignmentDTO | undefined;
  onSave: (body: { config: RecitationConfig }) => Promise<void>;
}) {
  const base = (() => {
    const src = assignment?.module_type === 'recitation' ? assignment.config.targets : null;
    const o = {} as Record<PrayerTime, number>;
    for (const p of PRAYER_ORDER) o[p] = src?.[p] ?? 5;
    return o;
  })();
  const [targets, setTargets] = useState<Record<PrayerTime, number>>(base);
  const [saving, setSaving] = useState(false);
  const dirty = PRAYER_ORDER.some((p) => targets[p] !== base[p]);

  const save = async () => {
    setSaving(true);
    await onSave({ config: { targets } });
    setSaving(false);
  };

  return (
    <div className="rounded-lg border-hairline border-border p-3">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
        {t('settings.recitation.title')}
      </div>
      <div className="grid grid-cols-5 gap-2">
        {PRAYER_ORDER.map((p) => (
          <label key={p} className="flex flex-col items-center gap-1">
            <span className="text-[10px] text-ink-muted">{t('prayer.' + p)}</span>
            <input
              type="number"
              min={1}
              max={50}
              value={targets[p]}
              onChange={(e) => setTargets((s) => ({ ...s, [p]: clampTarget(e.target.value) }))}
              className="h-10 w-full rounded-md border-hairline border-border-strong bg-surface px-1 text-center text-[14px] tabular-nums"
            />
          </label>
        ))}
      </div>
      <Button onClick={save} disabled={!dirty || saving} fullWidth className="mt-3">
        {t('common.save')}
      </Button>
    </div>
  );
}

function MemorizationSettings({
  assignment,
  surahs,
  onSave,
}: {
  assignment: AssignmentDTO | undefined;
  surahs: SurahMeta[];
  onSave: (body: { is_active: boolean; config: MemorizationConfig }) => Promise<void>;
}) {
  const cfg = assignment?.module_type === 'memorization' ? assignment.config : null;
  const [active, setActive] = useState(assignment?.is_active ?? true);
  const [surah, setSurah] = useState(cfg?.surah_number ?? 1);
  const [from, setFrom] = useState(cfg?.ayah_from ?? 1);
  const [to, setTo] = useState(cfg?.ayah_to ?? 15);
  const [slots, setSlots] = useState<MemorizationSlot[]>(
    cfg?.slots?.length ? cfg.slots.map((s) => ({ ...s })) : [],
  );
  const [saving, setSaving] = useState(false);

  const setSlot = (i: number, patch: Partial<MemorizationSlot>) =>
    setSlots((arr) => arr.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addSlot = () => setSlots((arr) => [...arr, { time: '12:00', target: 10 }]);
  const removeSlot = (i: number) => setSlots((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    const lo = Math.max(1, from);
    setSaving(true);
    await onSave({
      is_active: active,
      config: { surah_number: surah, ayah_from: lo, ayah_to: Math.max(lo, to), slots },
    });
    setSaving(false);
  };

  return (
    <div className="rounded-lg border-hairline border-border p-3">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
          {t('settings.memorization.title')}
        </div>
        <button
          type="button"
          onClick={() => setActive((v) => !v)}
          className={[
            'rounded-full px-3 py-1 text-[12px] font-medium transition active:scale-[0.98]',
            active ? 'bg-success-bg text-success' : 'bg-surface-muted text-ink-muted',
          ].join(' ')}
        >
          {active ? t('settings.module.on') : t('settings.module.off')}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-[11px] uppercase tracking-wide text-ink-subtle">
            {t('settings.mem.surah')}
          </span>
          <select
            value={surah}
            onChange={(e) => setSurah(Number(e.target.value))}
            className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px]"
          >
            {surahs.map((s) => (
              <option key={s.number} value={s.number}>
                {s.number}. {s.name_uz}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-ink-subtle">
            {t('settings.mem.ayah_from')}
          </span>
          <input
            type="number"
            min={1}
            value={from}
            onChange={(e) => setFrom(Math.max(1, Number(e.target.value) || 1))}
            className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px] tabular-nums"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-ink-subtle">
            {t('settings.mem.ayah_to')}
          </span>
          <input
            type="number"
            min={1}
            value={to}
            onChange={(e) => setTo(Math.max(1, Number(e.target.value) || 1))}
            className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px] tabular-nums"
          />
        </label>
      </div>

      <div className="mt-3 flex flex-col gap-2">
        <span className="text-[11px] uppercase tracking-wide text-ink-subtle">
          {t('settings.mem.slots')}
        </span>
        {slots.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="time"
              value={s.time}
              onChange={(e) => setSlot(i, { time: e.target.value })}
              className="h-10 rounded-md border-hairline border-border-strong bg-surface px-2 text-[14px] tabular-nums"
            />
            <input
              type="number"
              min={1}
              max={50}
              value={s.target}
              onChange={(e) => setSlot(i, { target: clampTarget(e.target.value) })}
              className="h-10 w-16 rounded-md border-hairline border-border-strong bg-surface px-2 text-center text-[14px] tabular-nums"
            />
            <span className="text-[12px] text-ink-muted">marta</span>
            <button
              type="button"
              onClick={() => removeSlot(i)}
              aria-label={t('common.delete')}
              className="ml-auto text-ink-muted active:scale-[0.95]"
            >
              <X size={16} />
            </button>
          </div>
        ))}
        <Button variant="ghost" onClick={addSlot} className="self-start">
          <Plus size={14} className="mr-1" />
          {t('settings.mem.add_slot')}
        </Button>
      </div>

      <Button onClick={save} disabled={saving} fullWidth className="mt-3">
        {t('common.save')}
      </Button>
    </div>
  );
}

function Field({ label, children, full = false }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <label className={['flex flex-col gap-1', full ? 'sm:col-span-2' : ''].join(' ')}>
      <span className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</span>
      {children}
    </label>
  );
}

function ChildPinModal({
  child,
  onClose,
  onSaved,
}: {
  child: ChildSettingsRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const valid = /^\d{4}$/.test(pin);
  const save = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await api.setChildPin(child.id, pin);
      onSaved();
    } catch {
      setSubmitting(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={`${child.name} — yangi PIN`}>
      <div className="flex flex-col gap-3">
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          autoFocus
          className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-center text-[18px] font-medium tracking-[0.5em] tabular-nums"
        />
        <div className="flex flex-col gap-2">
          <Button onClick={save} disabled={!valid || submitting} fullWidth>
            {t('common.save')}
          </Button>
          <Button variant="ghost" onClick={onClose} fullWidth>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ParentPinModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const valid = /^\d{6}$/.test(current) && /^\d{6}$/.test(next);
  const save = async () => {
    if (!valid) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.setParentPin(current, next);
      setCurrent('');
      setNext('');
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? t('pin.error.wrong') : t('common.error.network'));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} title="Ota-ona PIN">
      <div className="flex flex-col gap-3">
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={current}
          onChange={(e) => setCurrent(e.target.value.replace(/\D/g, ''))}
          placeholder="Joriy PIN"
          className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px] tabular-nums"
        />
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={next}
          onChange={(e) => setNext(e.target.value.replace(/\D/g, ''))}
          placeholder="Yangi PIN"
          className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px] tabular-nums"
        />
        {error ? <p className="text-[12px] text-danger">{error}</p> : null}
        <div className="flex flex-col gap-2">
          <Button onClick={save} disabled={!valid || submitting} fullWidth>
            {t('common.save')}
          </Button>
          <Button variant="ghost" onClick={onClose} fullWidth>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function RestDaysModal({
  open,
  childIds,
  onClose,
}: {
  open: boolean;
  childIds: Array<{ id: number; name: string }>;
  onClose: () => void;
}) {
  const toast = useToast();
  const [restDays, setRestDays] = useState<Array<{ child_id: number | null; quran_day: string; reason: string | null }>>([]);
  const [day, setDay] = useState(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Tashkent' }));
  const [reason, setReason] = useState('');
  const [target, setTarget] = useState<'all' | number>('all');

  const refresh = () => {
    if (!open) return;
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - 30);
    const to = new Date(today);
    to.setDate(to.getDate() + 30);
    api
      .getRestDays(
        from.toLocaleDateString('en-CA', { timeZone: 'Asia/Tashkent' }),
        to.toLocaleDateString('en-CA', { timeZone: 'Asia/Tashkent' }),
      )
      .then((r) => setRestDays(r.rest_days));
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  const add = async () => {
    try {
      await api.setRestDay({
        quran_day: day,
        child_id: target === 'all' ? null : target,
        reason: reason || undefined,
        action: 'add',
      });
      setReason('');
      refresh();
    } catch {
      toast.show(t('common.error.network'), 'danger');
    }
  };

  const remove = async (r: { child_id: number | null; quran_day: string }) => {
    try {
      await api.setRestDay({ quran_day: r.quran_day, child_id: r.child_id, action: 'remove' });
      refresh();
    } catch {
      toast.show(t('common.error.network'), 'danger');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('settings.rest_days')}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          <input
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px]"
          />
          <select
            value={String(target)}
            onChange={(e) => setTarget(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px]"
          >
            <option value="all">Barcha farzandlar</option>
            {childIds.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Sabab (ixtiyoriy)"
            maxLength={200}
            className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px]"
          />
          <Button onClick={add} fullWidth>
            Qoʻshish
          </Button>
        </div>

        <div className="flex flex-col gap-1 max-h-[240px] overflow-y-auto">
          {restDays.length === 0 ? (
            <p className="text-[12px] text-ink-muted">Hali yo'q</p>
          ) : (
            restDays.map((r) => (
              <div
                key={`${r.child_id ?? 'all'}|${r.quran_day}`}
                className="flex items-center justify-between rounded-md bg-surface-muted px-3 py-2"
              >
                <div className="text-[13px]">
                  <div className="tabular-nums">{r.quran_day}</div>
                  <div className="text-[11px] text-ink-muted">
                    {r.child_id == null ? 'Barcha farzandlar' : childIds.find((c) => c.id === r.child_id)?.name ?? '?'}
                    {r.reason ? ` · ${r.reason}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => remove(r)}
                  className="text-[12px] text-danger active:scale-[0.98]"
                >
                  {t('common.delete')}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

function AvatarUploader({ row }: { row: ChildSettingsRow }) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState<string | null>(row.avatar_url ?? null);

  useEffect(() => {
    setUrl(row.avatar_url ?? null);
  }, [row.avatar_url, row.id]);

  const onPick = async (file: File) => {
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.show(t('settings.avatar.unsupported'), 'danger');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.show(t('settings.avatar.too_large'), 'danger');
      return;
    }
    setBusy(true);
    try {
      const r = await api.uploadChildAvatar(row.id, file);
      setUrl(r.avatar_url);
      toast.show('Saqlandi', 'success');
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('common.error.network'), 'danger');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      await api.deleteChildAvatar(row.id);
      setUrl(null);
      toast.show(t('common.deleted'), 'success');
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('common.error.network'), 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className={`ramp-${row.color_ramp}`}>
        <Avatar initial={row.avatar_initial} ramp={row.color_ramp} size="lg" src={url ?? undefined} />
      </div>
      <div className="flex flex-col gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPick(f);
          }}
        />
        <Button
          variant="secondary"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          <ImageUp size={14} className="mr-1.5" />
          {url ? t('settings.avatar.replace') : t('settings.avatar.upload')}
        </Button>
        {url ? (
          <button
            onClick={onRemove}
            disabled={busy}
            className="self-start text-[12px] text-danger active:scale-[0.98] disabled:opacity-50"
          >
            {t('settings.avatar.remove')}
          </button>
        ) : null}
        <p className="text-[11px] text-ink-subtle">JPG, PNG, WebP · max 5 MB</p>
      </div>
    </div>
  );
}

function AddChildModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [ramp, setRamp] = useState<ColorRamp>('teal');
  const [initial, setInitial] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setAge('');
      setRamp('teal');
      setInitial('');
      setPin('');
    }
  }, [open]);

  useEffect(() => {
    if (!initial && name) setInitial(name.slice(0, 1).toUpperCase());
  }, [name, initial]);

  const valid =
    name.trim().length > 0 &&
    Number(age) >= 1 &&
    Number(age) <= 99 &&
    /^[A-Za-zÀ-ɏ]$/.test(initial) &&
    /^\d{4}$/.test(pin);

  const create = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await api.createChild({
        name: name.trim(),
        age: Number(age),
        color_ramp: ramp,
        avatar_initial: initial.toUpperCase(),
        pin,
      });
      onCreated();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('common.error.network'), 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('settings.add_child')}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-[11px] uppercase tracking-wide text-ink-subtle">Ismi</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            autoFocus
            className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-ink-subtle">Yoshi</span>
          <input
            type="number"
            min={1}
            max={99}
            value={age}
            onChange={(e) => setAge(e.target.value)}
            className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px] tabular-nums"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-ink-subtle">Avatar</span>
          <input
            value={initial}
            onChange={(e) => setInitial(e.target.value.slice(0, 1).toUpperCase())}
            maxLength={1}
            className="h-11 w-12 rounded-md border-hairline border-border-strong bg-surface px-3 text-center text-[16px] font-medium uppercase"
          />
        </label>
        <div className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-[11px] uppercase tracking-wide text-ink-subtle">Rang</span>
          <div className="flex gap-2">
            {RAMPS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRamp(r)}
                aria-label={r}
                className={[
                  'ramp-' + r,
                  'h-9 w-9 rounded-full bg-child-accent transition',
                  ramp === r ? 'ring-2 ring-info ring-offset-2 ring-offset-bg' : 'opacity-70',
                ].join(' ')}
              />
            ))}
          </div>
        </div>
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-[11px] uppercase tracking-wide text-ink-subtle">PIN (4 raqam)</span>
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-center text-[18px] font-medium tracking-[0.5em] tabular-nums"
          />
        </label>
        <div className="sm:col-span-2 flex flex-col gap-2 pt-1">
          <Button onClick={create} disabled={!valid || submitting} fullWidth>
            {submitting ? t('common.loading') : t('common.add')}
          </Button>
          <Button variant="ghost" onClick={onClose} fullWidth>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function DeleteChildModal({
  child,
  onClose,
  onDeleted,
}: {
  child: ChildSettingsRow;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const toast = useToast();
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const valid = confirmText.trim() === child.name;

  const remove = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await api.deleteChild(child.id);
      onDeleted();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('common.error.network'), 'danger');
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={t('settings.delete_child')}>
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-ink-muted">
          {t('settings.delete_child.warning', { name: child.name })}
        </p>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-ink-subtle">
            {t('settings.delete_child.confirm_label', { name: child.name })}
          </span>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="h-11 w-full rounded-md border-hairline border-border-strong bg-surface px-3 text-[14px]"
            autoFocus
          />
        </label>
        <div className="flex flex-col gap-2">
          <Button onClick={remove} disabled={!valid || submitting} fullWidth>
            {submitting ? t('common.loading') : t('common.delete')}
          </Button>
          <Button variant="ghost" onClick={onClose} fullWidth>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
